import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    setProgress,
    updatePollData,
    setUploadFailed,
    updateRowStatus,
    setGenaiRightsAccess,
} from "../../../../store/genaiRightsSlice";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import PaginationControls from "../../../utils/PaginationControls";
import ConfirmationModal from "../../../utils/ConfirmationModal";
import DocumentPreview from "../../../utils/DocumentPreview";
import FormUsersList from "../../teams/FormUsersList";
import * as XLSX from "xlsx";

/* ── Filter options ── */
const COL_FILTERS = [
    { id: "ALL",         label: "All" },
    { id: "OPEN_ACCESS", label: "Open Access" },
    { id: "UNPURCHASED", label: "Unpurchased" },
    { id: "ORDER_PLACED",label: "Order Placed" },
    { id: "PURCHASED",   label: "Purchased" },
];

/* ── Status pill — ev-* badge classes only ── */
const pill = (label, tone = "neutral") => {
    const cls = {
        good:    "discovery-badge-green",
        cost:    "discovery-badge-orange",
        muted:   "discovery-badge-unknown",
        neutral: "discovery-badge-unknown",
        blue:    "ev-badge-pill ev-badge-blue",
    }[tone] || "discovery-badge-unknown";
    return <span className={cls}>{label}</span>;
};

const isPdfOrderRequired = (r) => (
    Number(r?.delivery_price) > 0 &&
    r?.ft_status !== "ORDER_PLACED" &&
    !r?.ft_url
);

const fullTextState = (r) => {
    if (r?.ft_status === "ORDER_PLACED")               return { label: "Order Placed", tone: "cost" };
    if (r?.ft_status === "OPEN_ACCESS")                return { label: "Open Access",  tone: "good" };
    if (Number(r?.delivery_price) > 0 && !r?.ft_url)  return { label: "Unpurchased",  tone: "cost" };
    return { label: "Unknown", tone: "muted" };
};

const genAiState = (r) => {
    if (r?.genai_status === "PURCHASED")     return { label: "Purchased",    tone: "good" };
    if (r?.genai_status === "ORDER_PLACED")  return { label: "Order Placed", tone: "cost" };
    if (r?.genai_status === "OPEN_ACCESS")   return { label: "Open Access",  tone: "good" };
    if (r?.genai_status === "UNPURCHASED")   return { label: "Unpurchased",  tone: "cost" };
    return { label: "Unknown", tone: "muted" };
};


/* Long values — a full author list can run 30 names — would make every row
   tall enough to lose the table. Show a clipped version with a way to read
   the rest. */
const MAX_CELL_CHARS = 90;

const LongTextCell = ({ value, label, bold = false, onExpand }) => {
    if (!value) return <span className="ev-xsmall-p-dark opacity-50">—</span>;

    const text = String(value);
    if (text.length <= MAX_CELL_CHARS) {
        return <span className={`ev-xsmall-p-dark${bold ? " ev-bold-med" : ""}`}>{text}</span>;
    }

    return (
        <div className="d-flex flex-column align-items-center gap-1">
            <span className={`ev-xsmall-p-dark${bold ? " ev-bold-med" : ""}`}>
                {text.slice(0, MAX_CELL_CHARS).trimEnd()}…
            </span>
            <button
                type="button"
                className="border-0 bg-transparent p-0 ev-xsmall-p-dark ev-reg-p-blue"
                onClick={() => onExpand({ title: label, body: text })}
            >
                View more
            </button>
        </div>
    );
};

const BASE_URL = "/pceapi/slr/genai-rights";

const GenAIRightsDashboard = ({ check, onBack }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const headers  = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

    const jobId         = useSelector((s) => s.genaiRights.jobId);
    const jobStatus     = useSelector((s) => s.genaiRights.jobStatus);
    const name          = useSelector((s) => s.genaiRights.name);
    const fileName      = useSelector((s) => s.genaiRights.fileName);
    const ownerId       = useSelector((s) => s.genaiRights.ownerId);
    const ownerEmail    = useSelector((s) => s.genaiRights.ownerEmail);
    const genaiRightsAccess = useSelector((s) => s.genaiRights.genaiRightsAccess);
    const totalRows     = useSelector((s) => s.genaiRights.totalRows);
    const processedRows = useSelector((s) => s.genaiRights.processedRows);
    const results       = useSelector((s) => s.genaiRights.results);

    const [searchInput,  setSearchInput]  = useState("");
    const [searchQuery,  setSearchQuery]  = useState("");
    const [ftFilter,     setFtFilter]     = useState("ALL");
    const [genaiFilter,  setGenaiFilter]  = useState("ALL");
    const [ftDropOpen,    setFtDropOpen]    = useState(false);
    const [genaiDropOpen, setGenaiDropOpen] = useState(false);
    const ftFilterRef    = useRef(null);
    const genaiFilterRef = useRef(null);
    const [page,         setPage]         = useState(1);
    const [size,         setSize]         = useState(25);
    const [selectedRowNums, setSelectedRowNums] = useState(new Set());

    const [purchaseModal,           setPurchaseModal]           = useState({ open: false, paper: null });
    const [bulkPurchaseModal,       setBulkPurchaseModal]       = useState({ open: false, papers: [] });
    const [purchaseConfirmText,     setPurchaseConfirmText]     = useState("");
    const [bulkPurchaseConfirmText, setBulkPurchaseConfirmText] = useState("");
    const [previewModal, setPreviewModal] = useState({ open: false, url: null, name: null });
    const [cellModal,    setCellModal]    = useState({ open: false, title: "", body: "" });

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage,        setProcessingMessage]        = useState("");
    const [isSuccessPopupVisible,    setIsSuccessPopupVisible]    = useState(false);
    const [popupSuccessMessage,      setPopupSuccessMessage]      = useState("");
    const [isErrorPopupVisible,      setIsErrorPopupVisible]      = useState(false);
    const [popupErrorMessage,        setPopupErrorMessage]        = useState("");

    const [isRefreshing, setIsRefreshing] = useState(false);

    const [showAccessPanel, setShowAccessPanel] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const accessPanelRef = useRef(null);

    /* Keep the picker's selection in sync with whoever the check is shared
       with right now — otherwise reopening the panel after a save (or after
       another tab changed it) would show stale checkboxes. */
    useEffect(() => {
        setSelectedUserIds((genaiRightsAccess || []).map((u) => u.user_id));
    }, [genaiRightsAccess]);

    useEffect(() => {
        if (!showAccessPanel) return;
        const h = (e) => { if (accessPanelRef.current && !accessPanelRef.current.contains(e.target)) setShowAccessPanel(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [showAccessPanel]);

    const handleUserSelection = (userId) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    const saveAccess = async () => {
        const jId = check?.id ?? check?.job_id ?? jobId;
        if (!jId) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Saving access...");
        try {
            const res = await axios.post(`${BASE_URL}/save-user-access`,
                { job_id: jId, user_ids: selectedUserIds }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status === "success") {
                dispatch(setGenaiRightsAccess(res.data.data?.genai_rights_access || []));
                setPopupSuccessMessage("Access saved successfully");
                setShowAccessPanel(false);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to save access");
                setIsErrorPopupVisible(true);
            }
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || err.message || "Error saving access");
            setIsErrorPopupVisible(true);
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const streamRef     = useRef(null);
    const loadedRef     = useRef(null);   // job id whose saved results have been loaded
    const autoSelectRef = useRef(null);   // job id whose genai-eligible rows have been auto-selected

    const stopStream = useCallback(() => {
        if (streamRef.current?.abort) streamRef.current.abort();
        streamRef.current = null;
    }, []);

    useEffect(() => () => stopStream(), [stopStream]);

    useEffect(() => {
        if (!ftDropOpen) return;
        const h = (e) => { if (ftFilterRef.current && !ftFilterRef.current.contains(e.target)) setFtDropOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [ftDropOpen]);

    useEffect(() => {
        if (!genaiDropOpen) return;
        const h = (e) => { if (genaiFilterRef.current && !genaiFilterRef.current.contains(e.target)) setGenaiDropOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [genaiDropOpen]);

    /* ── Load saved rows ── */
    const loadResults = useCallback(async (jId) => {
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Loading rights check...");
        try {
            const res = await axios.post(`${BASE_URL}/results`, { job_id: jId }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return null; }
            if (res.data?.status === "success") {
                const d = res.data.data;
                dispatch(updatePollData({
                    status:        d.job_status,
                    name:          d.name,
                    fileName:      d.file_name,
                    totalRows:     d.total_rows,
                    processedRows: d.processed_rows,
                    results:       d.results || [],
                    ownerId:       d.owner_id,
                    ownerEmail:    d.owner_email,
                    genaiRightsAccess: d.genai_rights_access || [],
                }));
                return d;
            }
            setPopupErrorMessage(res.data?.message || "Could not load results");
            setIsErrorPopupVisible(true);
            return null;
        } catch (err) {
            console.error("GenAI rights results load error:", err);
            return null;
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    }, [headers, navigate, dispatch]);

    /* ── SSE — progress for a check that is still running ── */
    const startStream = useCallback((jId) => {
        stopStream();
        const controller = new AbortController();
        streamRef.current = controller;

        let gotEvent = false;
        // Dev-server and some reverse proxies buffer SSE responses: the connection
        // succeeds and the content type is right, but no chunk arrives until the
        // stream closes. If nothing lands within 6s, just wait for the whole body.
        const watchdog = setTimeout(() => {
            if (!gotEvent) {
                console.warn("GenAI SSE delivered nothing in 6s — awaiting full response instead");
                controller.abort();
                loadResults(jId);
            }
        }, 6000);

        const applyFinal = (data) => {
            dispatch(updatePollData({
                status:        "COMPLETED",
                name:          data.name,
                totalRows:     data.total_rows,
                processedRows: data.total_rows,
                results:       data.results || [],
            }));
            setPopupSuccessMessage(`Processed ${data.total_rows} papers successfully`);
            setIsSuccessPopupVisible(true);
        };

        fetch(`${BASE_URL}/status-stream`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: GetCookie("access_token") },
            body: JSON.stringify({ job_id: jId }),
            signal: controller.signal,
        }).then((response) => {
            const ctype = response.headers.get("content-type") || "";
            if (!response.ok || !response.body || !ctype.includes("text/event-stream")) {
                console.warn("GenAI SSE unavailable", response.status, ctype);
                clearTimeout(watchdog);
                loadResults(jId);
                return;
            }

            const reader  = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer    = "";   // holds partial lines across chunk boundaries

            const read = () => {
                reader.read().then(({ done, value }) => {
                    if (done) { clearTimeout(watchdog); return; }
                    gotEvent = true;
                    clearTimeout(watchdog);

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop();

                    lines.forEach((line) => {
                        const trimmed = line.trim();
                        if (!trimmed.startsWith("data:")) return;
                        try {
                            const data = JSON.parse(trimmed.slice(5).trim());
                            if (data.type === "progress") {
                                dispatch(setProgress({
                                    processed_rows: data.processed_rows,
                                    total_rows:     data.total_rows,
                                }));
                            }
                            if (data.type === "status") {
                                if (data.status === "COMPLETED") {
                                    controller.abort(); streamRef.current = null;
                                    applyFinal(data);
                                } else if (data.status === "FAILED") {
                                    controller.abort(); streamRef.current = null;
                                    dispatch(setUploadFailed());
                                    setPopupErrorMessage(data.error_message || "Processing failed");
                                    setIsErrorPopupVisible(true);
                                }
                            }
                        } catch (e) { console.warn("GenAI SSE parse error:", trimmed, e); }
                    });
                    read();
                }).catch((e) => {
                    clearTimeout(watchdog);
                    if (e.name !== "AbortError") {
                        console.error("GenAI SSE read error", e);
                        loadResults(jId);
                    }
                });
            };
            read();
        }).catch((e) => {
            clearTimeout(watchdog);
            if (e.name !== "AbortError") {
                console.error("GenAI SSE connect error", e);
                loadResults(jId);
            }
        });
    }, [dispatch, loadResults, stopStream]);

    /* On open: pull the saved rows, and follow progress if it is still running */
    useEffect(() => {
        const jId = check?.id ?? check?.job_id;
        if (!jId || loadedRef.current === jId) return;
        loadedRef.current = jId;

        (async () => {
            const d = await loadResults(jId);
            if (d?.job_status === "RUNNING") startStream(jId);
        })();
    }, [check, loadResults, startStream]);

    /* Pull the saved rows again, and follow progress if the check is still running */
    const handleRefresh = async () => {
        const jId = check?.id ?? check?.job_id ?? jobId;
        if (!jId || isRefreshing) return;
        setIsRefreshing(true);
        try {
            const d = await loadResults(jId);
            if (d?.job_status === "RUNNING" && !streamRef.current) startStream(jId);
        } finally {
            setIsRefreshing(false);
        }
    };

    /* ── Search, filters, paging ── */
    const filteredResults = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        return results.filter((r) => {
            const ftOk =
                ftFilter === "ALL" ||
                (ftFilter === "OPEN_ACCESS"  && r.ft_status === "OPEN_ACCESS") ||
                (ftFilter === "UNPURCHASED"  && isPdfOrderRequired(r)) ||
                (ftFilter === "ORDER_PLACED" && r.ft_status === "ORDER_PLACED") ||
                (ftFilter === "PURCHASED"    && r.ft_status === "PURCHASED");
            const genaiOk =
                genaiFilter === "ALL" ||
                (genaiFilter === "OPEN_ACCESS"  && r.genai_status === "OPEN_ACCESS") ||
                (genaiFilter === "UNPURCHASED"  && r.genai_status === "UNPURCHASED") ||
                (genaiFilter === "ORDER_PLACED" && r.genai_status === "ORDER_PLACED") ||
                (genaiFilter === "PURCHASED"    && r.genai_status === "PURCHASED");
            const haystack = [r.title, r.authors, r.doi, r.pmid].filter(Boolean).join(" ").toLowerCase();
            return ftOk && genaiOk && (!q || haystack.includes(q));
        });
    }, [results, searchQuery, ftFilter, genaiFilter]);

    const totalPages  = Math.ceil(filteredResults.length / size) || 1;
    const pageResults = filteredResults.slice((page - 1) * size, page * size);
    const progressPct = totalRows > 0 ? Math.min(100, Math.round((processedRows / totalRows) * 100)) : 0;

    const pageRowNums   = pageResults.map((r) => r.row_num);
    const isAllSelected = pageRowNums.length > 0 && pageRowNums.every((n) => selectedRowNums.has(n));
    const toggleAll     = () => setSelectedRowNums(isAllSelected ? new Set() : new Set(pageRowNums));
    const toggleRow     = (rowNum) => setSelectedRowNums((prev) => { const s = new Set(prev); s.has(rowNum) ? s.delete(rowNum) : s.add(rowNum); return s; });

    const purchasableGenai = useMemo(() => results.filter((r) => selectedRowNums.has(r.row_num) && r.genai_status === "UNPURCHASED" && r.genai_price != null), [results, selectedRowNums]);
    const genaiTotal = useMemo(() => purchasableGenai.reduce((s, r) => s + (r.genai_price ?? 0), 0), [purchasableGenai]);

    const genaiEligibleAll = useMemo(() => results.filter((r) => r.genai_status === "UNPURCHASED" && r.genai_price != null), [results]);
    const genaiEligibleAllTotal = useMemo(() => genaiEligibleAll.reduce((s, r) => s + (r.genai_price ?? 0), 0), [genaiEligibleAll]);

    const articlePurchaseAll = useMemo(() => results.filter((r) => isPdfOrderRequired(r) && r.delivery_price != null), [results]);
    const articlePurchaseTotal = useMemo(() => articlePurchaseAll.reduce((s, r) => s + (r.delivery_price ?? 0), 0), [articlePurchaseAll]);

    const rentalAll = useMemo(() => results.filter((r) => r.rental_price != null), [results]);
    const rentalTotal = useMemo(() => rentalAll.reduce((s, r) => s + (r.rental_price ?? 0), 0), [rentalAll]);

    /* Auto-select every genai-eligible paper as soon as results come in */
    useEffect(() => {
        if (results.length > 0 && jobId && autoSelectRef.current !== jobId) {
            autoSelectRef.current = jobId;
            setSelectedRowNums(new Set(genaiEligibleAll.map((r) => r.row_num)));
        }
    }, [results, jobId, genaiEligibleAll]);

    /* ── Purchase ── */
    const handlePurchase = (paper) => { setPurchaseConfirmText(""); setPurchaseModal({ open: true, paper }); };

    const handlePurchaseConfirm = async () => {
        if (purchaseConfirmText.trim() !== "purchase") { setPopupErrorMessage("Please type 'purchase' to confirm."); setIsErrorPopupVisible(true); return; }
        const { paper } = purchaseModal;
        setPurchaseModal({ open: false, paper: null }); setPurchaseConfirmText("");
        setIsProcessingPopupVisible(true); setProcessingMessage("Placing GenAI rights order…");
        try {
            const res = await axios.post(`${BASE_URL}/purchase`, { job_id: jobId, row_num: paper.row_num }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status === "success") {
                dispatch(updateRowStatus({ row_num: paper.row_num, genai_status: "ORDER_PLACED" }));
                setPopupSuccessMessage("Order placed successfully.");
                setIsSuccessPopupVisible(true);
            } else { setPopupErrorMessage(res.data?.message || "Failed."); setIsErrorPopupVisible(true); }
        } catch (err) { setPopupErrorMessage(err.response?.data?.message || "Error."); setIsErrorPopupVisible(true); }
        finally { setIsProcessingPopupVisible(false); setProcessingMessage(""); }
    };

    const handleBulkPurchase = () => {
        if (!purchasableGenai.length) { setPopupErrorMessage("No selected papers eligible."); setIsErrorPopupVisible(true); return; }
        setBulkPurchaseConfirmText(""); setBulkPurchaseModal({ open: true, papers: purchasableGenai });
    };

    const handleBulkPurchaseConfirm = async () => {
        if (bulkPurchaseConfirmText.trim() !== "purchase") { setPopupErrorMessage("Please type 'purchase' to confirm."); setIsErrorPopupVisible(true); return; }
        const { papers: toBuy } = bulkPurchaseModal;
        setBulkPurchaseModal({ open: false, papers: [] }); setBulkPurchaseConfirmText("");
        setIsProcessingPopupVisible(true); let ok = 0, fail = 0;
        for (const paper of toBuy) {
            setProcessingMessage(`Ordering ${ok + fail + 1} of ${toBuy.length}…`);
            try {
                const res = await axios.post(`${BASE_URL}/purchase`, { job_id: jobId, row_num: paper.row_num }, { headers });
                if (res.data?.status === "success") {
                    dispatch(updateRowStatus({ row_num: paper.row_num, genai_status: "ORDER_PLACED" }));
                    ok++;
                } else fail++;
            } catch { fail++; }
        }
        setIsProcessingPopupVisible(false); setProcessingMessage("");
        if (ok > 0) { setPopupSuccessMessage(`${ok} order(s) placed.${fail > 0 ? ` ${fail} failed.` : ""}`); setIsSuccessPopupVisible(true); setSelectedRowNums(new Set()); }
        else { setPopupErrorMessage("All orders failed."); setIsErrorPopupVisible(true); }
    };

    /* ── Download ── */
    const handleDownloadExcel = () => {
        if (!results.length) return;
        const yn = (v) => (v == null ? "" : v ? "Yes" : "No");
        const rows = results.map((r) => ({
            "#":                     r.row_num,
            "Title":                 r.title   || "",
            "Authors":               r.authors || "",
            "Journal":               r.journal || "",
            "Year":                  r.year    || "",
            "Volume":                r.volume  || "",
            "Issue":                 r.issue   || "",
            "Pages":                 r.pages   || "",
            "Publisher":             r.publisher || "",
            "ISSN":                  r.issn    || "",
            "DOI":                   r.doi     || "",
            "PMID":                  r.pmid    || "",
            "PCEID":                 r.pceid   || "",
            "Full Text Status":      fullTextState(r).label,
            "Full Text URL":         r.ft_url    || "",
            "Open Access Status":    r.open_access_status || "",
            "Delivery Price ($)":    r.delivery_price ?? "",
            "Quick Download":        yn(r.quick_download),
            "Rental Price ($)":      r.rental_price ?? "",
            "PDF Library Access":    yn(r.pdf_library_access),
            "Subscription Access":   yn(r.subscription_access),
            "Token Access":          yn(r.token_access),
            "GenAI Status":          genAiState(r).label,
            "Is GenAI Available":    yn(r.is_genai_available),
            "GenAI Availability":    r.genai_availability || "",
            "GenAI Price ($)":       r.genai_price ?? "",
            "GenAI Order ID":        r.genai_order_id || "",
            "GenAI Fields Disagree": r.genai_conflict || "",
            "Note":                  r.error_message || "",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Rights Results");
        XLSX.writeFile(wb, `${(name || fileName || "genai_rights").replace(/\.[^/.]+$/, "")}_results.xlsx`);
    };

    const isRunning  = jobStatus === "RUNNING";
    const hasResults = results.length > 0;

    /* ══ RENDER ══ */
    return (
        <div className="m-4 d-flex flex-column">

            {/* Title + share + back */}
            <div className="d-flex align-items-center mb-3">
                <p className="ev-med-p-dark-blue mb-0">{name || check?.name || "Rights Check"}</p>

                <div className="d-flex gap-2 ms-auto position-relative" ref={accessPanelRef}>
                    {ownerId && (
                        <button
                            type="button"
                            className="ev-btn-lightblue-fill-round ev-btn-reg ev-bold-med"
                            onClick={(e) => { e.stopPropagation(); setShowAccessPanel((prev) => !prev); }}
                        >
                            <i className="bi bi-people-fill"></i> Rights Check Access
                        </button>
                    )}

                    {showAccessPanel && (
                        <div
                            className="ev-card-4 p-2 position-absolute top-100 end-0"
                            style={{ zIndex: 1050, width: 600 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-0 px-2 mt-1">
                                <p className="ev-reg-p-dark-blue ev-bold-med mb-0">
                                    Provide User Access
                                </p>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowAccessPanel(false)}
                                />
                            </div>

                            <FormUsersList
                                apiBaseUrl={BASE_URL}
                                selectedUsers={selectedUserIds}
                                onSelectUser={handleUserSelection}
                                ownerEmail={ownerEmail}
                            />

                            <div className="d-flex justify-content-end align-items-center mt-0">
                                <button
                                    type="button"
                                    className="ev-btn-blue-fill-round ev-btn-small"
                                    onClick={saveAccess}
                                    disabled={selectedUserIds.length === 0}
                                >
                                    Save Access
                                </button>
                            </div>
                        </div>
                    )}

                    <button className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg" onClick={onBack} type="button">
                       Back
                    </button>
                </div>
            </div>

            <div className="ev-card-4 d-flex flex-column overflow-hidden flex-grow-1 vh-70">

                {/* Toolbar */}
                <div className="d-flex align-items-center gap-2 px-3 py-3 border-bottom">
                    <div className="position-relative flex-grow-1">
                        <input className="form-control search-input ms-0" value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { setSearchQuery(searchInput); setPage(1); } }}
                            placeholder="Search title, author, DOI or PMID" />
                        <button className="ev-btn-lightblue-fill-round search-bar-button"
                            onClick={() => { setSearchQuery(searchInput); setPage(1); }}>
                            <i className="bi bi-search"></i>
                        </button>
                    </div>
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-med"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        <i className="bi bi-arrow-repeat" style={{ animation: "none" }}></i> Refresh
                    </button>

                    {hasResults && (
                        <button className="ev-btn-blue-outline-round ev-btn-reg flex-shrink-0 d-flex align-items-center gap-1"
                            onClick={handleDownloadExcel}>
                            <i className="bi bi-file-earmark-excel"></i> Download
                        </button>
                    )}
                </div>

                {/* Still running with rows already showing — the table fills in
                    column by column, so be explicit that it is not final yet. */}
                {isRunning && hasResults && (
                    <div className="d-flex align-items-center gap-3 px-3 py-2 border-bottom ev-message-card1">
                        <span className="spinner-border spinner-border-sm text-primary flex-shrink-0" role="status" />
                        <div className="flex-grow-1">
                            <span className="ev-xsmall-p-dark ev-bold-med">
                                Still checking — {processedRows} of {totalRows} papers done
                            </span>
                            <span className="ev-xsmall-p-dark ms-2 opacity-75">
                                Prices and rights appear as each paper is checked. Refresh to see the latest.
                            </span>
                        </div>
                        <span className="ev-xsmall-p-dark ev-bold-med flex-shrink-0">{progressPct}%</span>
                    </div>
                )}

                {/* Processing — centered */}
                {isRunning && !hasResults && (
                    <div className="d-flex align-items-center justify-content-center flex-grow-1 py-5">
                        <div className="text-center w-50">
                            <span className="spinner-border text-primary mb-3" role="status" />
                            <p className="ev-small-p-dark ev-bold-med mb-1">Checking rights</p>
                            <p className="ev-xsmall-p-dark opacity-75 mb-3">
                                {processedRows} of {totalRows} papers processed
                            </p>
                            <div className="progress ev-radius-xsmall">
                                <div className="progress-bar bg-primary ev-radius-xsmall"
                                    role="progressbar"
                                    style={{ width: `${progressPct}%` }}
                                    aria-valuenow={progressPct} aria-valuemin="0" aria-valuemax="100" />
                            </div>
                            <p className="ev-xsmall-p-dark ev-bold-med mt-2 mb-0">{progressPct}%</p>
                        </div>
                    </div>
                )}

                {/* KPI strip */}
                {!isRunning && hasResults && (
                    <div className="row gx-0 border-bottom">
                        {[
                            { k: "GenAI Licences to Buy", price: genaiEligibleAllTotal, count: genaiEligibleAll.length, unit: "article" },
                            { k: "Article Purchase",      price: articlePurchaseTotal,  count: articlePurchaseAll.length, unit: "priced" },
                            { k: "Rental Instead",        price: rentalTotal,           count: rentalAll.length, unit: "rentable" },
                        ].map(({ k, price, count, unit }, i, arr) => (
                            <div key={k} className={`col px-3 py-3${i < arr.length - 1 ? " border-end" : ""}`}>
                                <div className="ev-xsmall-p-dark ev-bold-med text-uppercase">{k}</div>
                                <div className="ev-med-p-dark ev-bold-med mt-2">${price.toFixed(2)}</div>
                                <div className="ev-xsmall-p-dark mt-1">{count} {unit === "article" ? (count === 1 ? unit : "articles") : unit}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Results */}
                {hasResults && (
                    <>
                        {/* Selection actions */}
                        {selectedRowNums.size > 0 && (
                            <div className="d-flex align-items-center gap-2 px-3 py-2">
                                <span className="ev-badge-pill ev-badge-blue">{selectedRowNums.size} selected</span>
                                {purchasableGenai.length > 0 && (
                                    <button className="ev-btn-lightblue-fill-round ev-btn-small ms-auto"
                                        onClick={handleBulkPurchase}>
                                        Purchase GenAI rights ({purchasableGenai.length}) — ${genaiTotal.toFixed(2)}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Wide table — scrolls both ways, header stays aligned */}
                        {/* One scroll container (viewport-sized) handles both
                            axes, so its scrollbar always renders at the card's
                            true edges — see the comment on that div below. */}
                        <div className="flex-grow-1 overflow-hidden px-2 pb-2">
                            {/* Single scroll container for BOTH axes — it stays
                                viewport-sized (w-100 h-100), so its scrollbar
                                stays pinned to the card's true edges instead of
                                drifting with content, every row is reachable
                                (overflowY auto, not hidden), and horizontal
                                panning still reaches the last column (overflowX
                                auto on this same div, not hidden). The inner div
                                below only forces the content width (2240px) —
                                it does no scrolling itself. scrollable-element's
                                webkit rule hides the horizontal bar but keeps
                                panning via trackpad/shift+wheel/drag; the
                                vertical bar stays visible. */}
                            <div className="w-100 h-100 scrollable-element"
                                style={{ overflowY: "auto", overflowX: "auto" }}>
                                <div className="pe-1" style={{ minWidth: 2240 }}>
                                    <table className="table-custom1 w-100">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }} className="text-center align-middle">
                                            <input type="checkbox" className="form-check-input mt-0"
                                                checked={isAllSelected} onChange={toggleAll} />
                                        </th>
                                        <th style={{ width: 48 }} className="align-middle text-center">#</th>
                                        <th style={{ minWidth: 320 }} className="align-middle text-center">Title</th>
                                        <th style={{ minWidth: 220 }} className="align-middle text-center">Authors</th>
                                        <th style={{ minWidth: 180 }} className="align-middle text-center">Journal</th>
                                        <th style={{ width: 70 }} className="align-middle text-center">Year</th>
                                        <th style={{ width: 70 }} className="align-middle text-center">Vol</th>
                                        <th style={{ width: 70 }} className="align-middle text-center">Issue</th>
                                        <th style={{ width: 90 }} className="align-middle text-center">Pages</th>
                                        <th style={{ minWidth: 160 }} className="align-middle text-center">Publisher</th>
                                        <th style={{ width: 110 }} className="align-middle text-center">ISSN</th>
                                        <th style={{ minWidth: 180 }} className="align-middle text-center">DOI</th>
                                        <th style={{ width: 100 }} className="align-middle text-center">PMID</th>
                                        <th style={{ width: 90 }} className="align-middle text-center">PCEID</th>
                                        <th style={{ minWidth: 150 }} className="position-relative align-middle text-center" ref={ftFilterRef}>
                                            <button className="border-0 bg-transparent d-flex align-items-center justify-content-center gap-1 p-0 mx-auto" style={{ color: "inherit", font: "inherit" }}
                                                onClick={() => setFtDropOpen((o) => !o)}>
                                                Full Text
                                                <i className={`bi bi-funnel-fill ev-fs-11${ftFilter !== "ALL" ? " text-primary" : ""}`}></i>
                                            </button>
                                            {ftDropOpen && (
                                                <div className="position-absolute ev-card-4 ev-radius-small discovery-filter-dropdown p-1"
                                                    style={{ top: "calc(100% + 4px)", left: 0, zIndex: 20, minWidth: 150 }}>
                                                    {COL_FILTERS.map((f) => (
                                                        <div key={f.id}
                                                            className={`discovery-filter-option ev-radius-small px-3 py-2 ev-xsmall-p-dark ${ftFilter === f.id ? "ev-btn-blue-fill-round ev-bold-med" : ""}`}
                                                            onClick={() => { setFtFilter(f.id); setPage(1); setFtDropOpen(false); }}>
                                                            {f.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                        <th style={{ width: 130 }} className="align-middle text-center">Open Access</th>
                                        <th style={{ width: 100 }} className="align-middle text-center">Delivery $</th>
                                        <th style={{ width: 100 }} className="align-middle text-center">Rental $</th>
                                        <th style={{ width: 100 }} className="text-center align-middle">Quick DL</th>
                                        <th style={{ width: 100 }} className="text-center align-middle">Library</th>
                                        <th style={{ width: 110 }} className="text-center align-middle">Subscription</th>
                                        <th style={{ width: 90 }}  className="text-center align-middle">Token</th>
                                        <th style={{ minWidth: 170 }} className="position-relative align-middle text-center" ref={genaiFilterRef}>
                                            <button className="border-0 bg-transparent d-flex align-items-center justify-content-center gap-1 p-0 mx-auto" style={{ color: "inherit", font: "inherit" }}
                                                onClick={() => setGenaiDropOpen((o) => !o)}>
                                                GenAI Rights
                                                <i className={`bi bi-funnel-fill ev-fs-11${genaiFilter !== "ALL" ? " text-primary" : ""}`}></i>
                                            </button>
                                            {genaiDropOpen && (
                                                <div className="position-absolute ev-card-4 ev-radius-small discovery-filter-dropdown p-1"
                                                    style={{ top: "calc(100% + 4px)", left: 0, zIndex: 20, minWidth: 150 }}>
                                                    {COL_FILTERS.map((f) => (
                                                        <div key={f.id}
                                                            className={`discovery-filter-option ev-radius-small px-3 py-2 ev-xsmall-p-dark ${genaiFilter === f.id ? "ev-btn-blue-fill-round ev-bold-med" : ""}`}
                                                            onClick={() => { setGenaiFilter(f.id); setPage(1); setGenaiDropOpen(false); }}>
                                                            {f.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                        <th style={{ width: 110 }} className="text-center align-middle">GenAI Avail</th>
                                        <th style={{ width: 130 }} className="align-middle text-center">Availability</th>
                                        <th style={{ width: 150 }} className="align-middle text-center">GenAI $</th>
                                        <th style={{ minWidth: 200 }} className="align-middle text-center">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageResults.length === 0 ? (
                                        <tr>
                                            <td colSpan="27" className="text-center py-5">
                                                <i className="bi bi-journal-x ev-fs-36 ev-reg-p-blue d-block mb-2"></i>
                                                <span className="ev-small-p-dark">No results match the current filters.</span>
                                            </td>
                                        </tr>
                                    ) : pageResults.map((r) => {
                                        const selected = selectedRowNums.has(r.row_num);
                                        const ft = fullTextState(r);
                                        const ga = genAiState(r);
                                        const yn = (v) => v == null
                                            ? <span className="ev-xsmall-p-dark opacity-50">—</span>
                                            : <span className="ev-xsmall-p-dark">{v ? "Yes" : "No"}</span>;
                                        const money = (v) => v == null
                                            ? <span className="ev-xsmall-p-dark opacity-50">—</span>
                                            : <span className="ev-xsmall-p-dark">${Number(v).toFixed(2)}</span>;
                                        const text = (v) => v
                                            ? <span className="ev-xsmall-p-dark">{v}</span>
                                            : <span className="ev-xsmall-p-dark opacity-50">—</span>;

                                        return (
                                            <tr key={r.row_num} className={selected ? "discovery-row-active" : ""}>
                                                <td className="text-center align-middle">
                                                    <input type="checkbox" className="form-check-input mt-0"
                                                        checked={selected} onChange={() => toggleRow(r.row_num)} />
                                                </td>
                                                <td className="align-middle text-center"><span className="ev-xsmall-p-dark">{r.row_num}</span></td>
                                                <td className="align-middle text-center">
                                                    <LongTextCell value={r.title || "Untitled"} label="Title" bold
                                                        onExpand={(d) => setCellModal({ open: true, ...d })} />
                                                </td>
                                                <td className="align-middle text-center">
                                                    <LongTextCell value={r.authors} label="Authors"
                                                        onExpand={(d) => setCellModal({ open: true, ...d })} />
                                                </td>
                                                <td className="align-middle text-center">
                                                    <LongTextCell value={r.journal} label="Journal"
                                                        onExpand={(d) => setCellModal({ open: true, ...d })} />
                                                </td>
                                                <td className="align-middle text-center">{text(r.year)}</td>
                                                <td className="align-middle text-center">{text(r.volume)}</td>
                                                <td className="align-middle text-center">{text(r.issue)}</td>
                                                <td className="align-middle text-center">{text(r.pages)}</td>
                                                <td className="align-middle text-center">
                                                    <LongTextCell value={r.publisher} label="Publisher"
                                                        onExpand={(d) => setCellModal({ open: true, ...d })} />
                                                </td>
                                                <td className="align-middle text-center">{text(r.issn)}</td>
                                                <td className="align-middle text-center">{text(r.doi)}</td>
                                                <td className="align-middle text-center">{text(r.pmid)}</td>
                                                <td className="align-middle text-center">{text(r.pceid)}</td>
                                                <td className="align-middle text-center">
                                                    {r.ft_url ? (
                                                        <span style={{ cursor: "pointer" }} title="Click to view PDF"
                                                            onClick={() => setPreviewModal({ open: true, url: r.ft_url, name: r.title || r.doi || "Document" })}>
                                                            {pill(ft.label, ft.tone)}
                                                        </span>
                                                    ) : pill(ft.label, ft.tone)}
                                                </td>
                                                <td className="align-middle text-center">{text(r.open_access_status)}</td>
                                                <td className="align-middle text-center">{money(r.delivery_price)}</td>
                                                <td className="align-middle text-center">{money(r.rental_price)}</td>
                                                <td className="align-middle text-center">{yn(r.quick_download)}</td>
                                                <td className="align-middle text-center">{yn(r.pdf_library_access)}</td>
                                                <td className="align-middle text-center">{yn(r.subscription_access)}</td>
                                                <td className="align-middle text-center">{yn(r.token_access)}</td>
                                                <td className="align-middle text-center">
                                                    {pill(ga.label, ga.tone)}
                                                </td>
                                                <td className="align-middle text-center">{yn(r.is_genai_available)}</td>
                                                <td className="align-middle text-center">{text(r.genai_availability)}</td>
                                                <td className="align-middle text-center">
                                                    {r.genai_status === "UNPURCHASED" && r.genai_price != null ? (
                                                        <button className="ev-btn-blue-fill-round ev-btn-xsmall text-nowrap"
                                                            onClick={() => handlePurchase(r)}>
                                                            Buy ${Number(r.genai_price).toFixed(2)}
                                                        </button>
                                                    ) : money(r.genai_price)}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {r.genai_conflict
                                                        ? <button type="button"
                                                            className="border-0 bg-transparent p-0 ev-xsmall-p-dark"
                                                            onClick={() => setCellModal({ open: true, title: "Note", body: r.genai_conflict })}>
                                                            <i className="bi bi-exclamation-triangle me-1 text-warning"></i>Fields disagree
                                                          </button>
                                                        : <LongTextCell value={r.error_message} label="Note"
                                                            onExpand={(d) => setCellModal({ open: true, ...d })} />}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                                </div>
                            </div>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex align-items-center justify-content-between px-3 py-3 border-top">
                            <span className="ev-xsmall-p-dark">Page {page} of {totalPages} · {filteredResults.length} papers</span>
                            <div className="d-flex align-items-center gap-2">
                                <button className="ev-btn-light-fill ev-btn-xsmall" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <button className="ev-btn-light-fill ev-btn-xsmall" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                                <PaginationControls initialSize={size} onSizeChange={(s) => { setSize(s); setPage(1); }} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* PDF Preview */}
            {previewModal.open && previewModal.url && (
                <DocumentPreview
                    isOpen={previewModal.open}
                    onClose={() => setPreviewModal({ open: false, url: null, name: null })}
                    presigned_url={previewModal.url}
                    documentName={previewModal.name}
                    showOpenInTab={true}
                />
            )}

            {/* Full value of a clipped cell */}
            <ConfirmationModal show={cellModal.open}
                onClose={() => setCellModal({ open: false, title: "", body: "" })}
                onConfirm={() => setCellModal({ open: false, title: "", body: "" })}
                title={cellModal.title}
                message=""
                confirmButtonText="Close" confirmButtonClass="btn-primary"
                bodyContent={
                    <p className="ev-small-p-dark mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {cellModal.body}
                    </p>
                } />

            {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
            {isSuccessPopupVisible && <SuccessPopup message={popupSuccessMessage} onClose={() => setIsSuccessPopupVisible(false)} />}
            {isErrorPopupVisible   && <ErrorPopup   message={popupErrorMessage}   onClose={() => setIsErrorPopupVisible(false)} />}

            <ConfirmationModal show={purchaseModal.open}
                onClose={() => { setPurchaseModal({ open: false, paper: null }); setPurchaseConfirmText(""); }}
                onConfirm={handlePurchaseConfirm}
                title="Purchase GenAI Rights"
                message={purchaseModal.paper?.genai_price != null
                    ? `This will place a paid order for GenAI rights at $${Number(purchaseModal.paper.genai_price).toFixed(2)}.`
                    : "Place a GenAI rights order for this paper?"}
                confirmButtonText="Place Order" confirmButtonClass="btn-primary"
                bodyContent={
                    <div>
                        <label className="ev-reg-p-dark mb-1 d-block">Type <b>purchase</b> to confirm</label>
                        <input type="text" className="formField6 w-100" value={purchaseConfirmText}
                            onChange={(e) => setPurchaseConfirmText(e.target.value)}
                            onPaste={(e) => e.preventDefault()} placeholder="purchase" autoComplete="off" />
                    </div>
                } />

            <ConfirmationModal show={bulkPurchaseModal.open}
                onClose={() => { setBulkPurchaseModal({ open: false, papers: [] }); setBulkPurchaseConfirmText(""); }}
                onConfirm={handleBulkPurchaseConfirm}
                title="Purchase GenAI Rights"
                message={`This will place paid orders for ${bulkPurchaseModal.papers.length} paper(s)${genaiTotal > 0 ? ` totalling $${genaiTotal.toFixed(2)}` : ""}.`}
                confirmButtonText="Place Orders" confirmButtonClass="btn-primary"
                bodyContent={
                    <div>
                        <label className="ev-reg-p-dark mb-1 d-block">Type <b>purchase</b> to confirm</label>
                        <input type="text" className="formField6 w-100" value={bulkPurchaseConfirmText}
                            onChange={(e) => setBulkPurchaseConfirmText(e.target.value)}
                            onPaste={(e) => e.preventDefault()} placeholder="purchase" autoComplete="off" />
                    </div>
                } />
        </div>
    );
};

export default GenAIRightsDashboard;